import type { Cents } from "../domain/money";
import type { CostType } from "../domain/proposedCostLine";
import type {
  DocumentKind,
  ExtractedDocument,
  ExtractedLine,
  ExtractedValue,
  SourceRegion,
} from "./interpreter";

/**
 * Azure AI Document Intelligence response mapping (Phase 9D) — PURE.
 *
 * Chosen because it is the practical fit for this phase's hard
 * requirements: prebuilt receipt AND invoice models, per-field
 * `boundingRegions` (page number + polygon) and `confidence` — which is
 * what makes "click the actual, see the exact line highlighted on the
 * receipt" possible — and a single API-key header, so the credential can
 * live behind the same platform transport as the Phase 9B.1 sourcing key.
 *
 * Everything here is a pure function over the provider's JSON so the tests
 * exercise it against recorded fixtures and never spend API pages.
 * Nothing is invented: a field the model did not return stays null.
 */

export const AZURE_PROVIDER_ID = "azure-document-intelligence";
export const AZURE_API_VERSION = "2024-11-30";

/** prebuilt model for each document kind we support. */
export function modelIdFor(kind: DocumentKind): string {
  return kind === "invoice" ? "prebuilt-invoice" : "prebuilt-receipt";
}

/** The layout model used for plain-text reads (loan agreements, spec §73D). */
export const AZURE_LAYOUT_MODEL = "prebuilt-layout";

/**
 * The document's plain text out of a layout/read analyze result. Pure;
 * an absent content field is an empty string, never an invention.
 */
export function plainTextOf(json: unknown): string {
  const root = ((json ?? {}) as Record<string, unknown>).analyzeResult ?? json ?? {};
  const content = (root as { content?: unknown }).content;
  return typeof content === "string" ? content : "";
}

interface AzureField {
  type?: string;
  content?: string;
  confidence?: number;
  valueString?: string;
  valueNumber?: number;
  valueDate?: string;
  valueCurrency?: { amount?: number; currencyCode?: string };
  valueArray?: AzureField[];
  valueObject?: Record<string, AzureField>;
  boundingRegions?: Array<{ pageNumber?: number; polygon?: number[] }>;
}

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);

/** Money → integer cents, never floating point in the domain. */
function fieldToCents(field: AzureField | undefined): Cents | null {
  if (!field) return null;
  const amount = num(field.valueCurrency?.amount) ?? num(field.valueNumber);
  if (amount !== null) return Math.round(amount * 100);
  // Fall back to the printed text when the model gave no typed value.
  const text = (field.content ?? "").replace(/[^0-9.\-]/g, "");
  if (text === "" || Number.isNaN(Number(text))) return null;
  return Math.round(Number(text) * 100);
}

function regionOf(field: AzureField | undefined, pages: Map<number, { width: number | null; height: number | null; unit: string }>): SourceRegion | null {
  const region = field?.boundingRegions?.[0];
  if (!region || !Array.isArray(region.polygon) || region.polygon.length < 4) return null;
  const pageNumber = num(region.pageNumber) ?? 1;
  const page = pages.get(pageNumber);
  return {
    pageNumber,
    polygon: region.polygon.filter((n): n is number => typeof n === "number"),
    pageWidth: page?.width ?? null,
    pageHeight: page?.height ?? null,
    pageUnit: page?.unit ?? "inch",
  };
}

function value<T>(field: AzureField | undefined, parsed: T | null, pages: Parameters<typeof regionOf>[1]): ExtractedValue<T> {
  return {
    value: parsed,
    raw: field?.content ?? "",
    confidence: num(field?.confidence),
    region: regionOf(field, pages),
  };
}

const EMPTY = <T,>(): ExtractedValue<T> => ({ value: null, raw: "", confidence: null, region: null });

/**
 * Invoice line items can be labor. The model does not classify them, so we
 * only claim a basis when the text says so plainly — otherwise the basis
 * stays null and the reviewer chooses. A receipt line is never assumed to
 * be labor.
 */
export function inferCostType(description: string, kind: DocumentKind): CostType | null {
  if (kind !== "invoice") return "material";
  const d = description.toLowerCase();
  if (/\b(labor|labour|installation|install charge|service call|workmanship)\b/.test(d)) return "labor";
  if (/\b(material|materials|parts|supplies)\b/.test(d)) return "material";
  return null;
}

/**
 * Map an Azure `analyzeResult` into our provider-neutral candidate
 * document. Handles both prebuilt-receipt and prebuilt-invoice field
 * names, since a household receipt and a contractor invoice use different
 * schemas for the same ideas.
 */
export function parseAnalyzeResult(json: unknown, kind: DocumentKind): ExtractedDocument {
  const root = ((json ?? {}) as Record<string, unknown>).analyzeResult ?? json ?? {};
  const result = root as Record<string, unknown>;
  const apiVersion = str(result.apiVersion) ?? AZURE_API_VERSION;
  const modelId = str(result.modelId) ?? modelIdFor(kind);

  const pageList = Array.isArray(result.pages) ? (result.pages as Array<Record<string, unknown>>) : [];
  const pages = new Map<number, { width: number | null; height: number | null; unit: string }>();
  for (const page of pageList) {
    const n = num(page.pageNumber) ?? 1;
    pages.set(n, { width: num(page.width), height: num(page.height), unit: str(page.unit) ?? "inch" });
  }

  const documents = Array.isArray(result.documents) ? (result.documents as Array<Record<string, unknown>>) : [];
  const fields = (documents[0]?.fields ?? {}) as Record<string, AzureField>;

  // Receipt and invoice schemas name the same ideas differently.
  const vendorField = fields.MerchantName ?? fields.VendorName;
  const dateField = fields.TransactionDate ?? fields.InvoiceDate;
  const numberField = fields.ReceiptNumber ?? fields.InvoiceId ?? fields.PurchaseOrder;
  const subtotalField = fields.Subtotal ?? fields.SubTotal;
  const taxField = fields.TotalTax ?? fields.Tax;
  const totalField = fields.Total ?? fields.InvoiceTotal ?? fields.AmountDue;
  const shippingField = fields.ShippingCost ?? fields.Freight;
  const discountField = fields.TotalDiscount ?? fields.Discount;
  const paymentField = fields.PaymentDetails ?? fields.PaymentTerm;

  const itemsField = fields.Items;
  const rawItems = Array.isArray(itemsField?.valueArray) ? itemsField!.valueArray! : [];
  const lines: ExtractedLine[] = rawItems.map((item) => {
    const o = (item.valueObject ?? {}) as Record<string, AzureField>;
    const description = str(o.Description?.valueString) ?? str(o.Description?.content) ?? str(o.Name?.valueString) ?? "";
    const quantity = num(o.Quantity?.valueNumber);
    const unitPrice = fieldToCents(o.Price ?? o.UnitPrice);
    const amount = fieldToCents(o.TotalPrice ?? o.Amount);
    return {
      description: value(o.Description ?? o.Name, description === "" ? null : description, pages),
      sku: value(o.ProductCode, str(o.ProductCode?.valueString) ?? str(o.ProductCode?.content), pages),
      // Azure does not model UPC; keep the slot so a future model can fill
      // it rather than pretending we have one.
      upc: EMPTY<string>(),
      quantityX100: value(o.Quantity, quantity === null ? null : Math.round(quantity * 100), pages),
      unit: value(o.Unit, str(o.Unit?.valueString) ?? str(o.Unit?.content), pages),
      unitPriceCents: value(o.Price ?? o.UnitPrice, unitPrice, pages),
      amountCents: value(o.TotalPrice ?? o.Amount, amount, pages),
      costType: inferCostType(description, kind),
      region: regionOf(item, pages) ?? regionOf(o.Description ?? o.Name, pages),
      confidence: num(item.confidence) ?? num(o.Description?.confidence) ?? null,
    };
  });

  return {
    kind,
    providerId: AZURE_PROVIDER_ID,
    modelId,
    apiVersion,
    pageCount: pages.size || pageList.length,
    vendor: value(vendorField, str(vendorField?.valueString) ?? str(vendorField?.content), pages),
    documentDate: value(dateField, str(dateField?.valueDate) ?? str(dateField?.content), pages),
    receiptNumber: value(numberField, str(numberField?.valueString) ?? str(numberField?.content), pages),
    subtotalCents: value(subtotalField, fieldToCents(subtotalField), pages),
    discountCents: value(discountField, fieldToCents(discountField), pages),
    taxCents: value(taxField, fieldToCents(taxField), pages),
    shippingCents: value(shippingField, fieldToCents(shippingField), pages),
    totalCents: value(totalField, fieldToCents(totalField), pages),
    paymentMethod: value(paymentField, str(paymentField?.valueString) ?? str(paymentField?.content), pages),
    lines,
  };
}

/**
 * Is this document a return/refund? Negative totals and explicit wording
 * are the only evidence we trust; anything else stays a purchase and the
 * reviewer decides (spec §33A: ambiguity asks, never guesses).
 */
export function looksLikeReturn(doc: ExtractedDocument): boolean {
  const total = doc.totalCents.value;
  if (total !== null && total < 0) return true;
  const text = `${doc.vendor.raw} ${doc.receiptNumber.raw} ${doc.lines.map((l) => l.description.raw).join(" ")}`.toLowerCase();
  return /\b(return|refund|credit memo|rma)\b/.test(text);
}
